import type { Program } from '@vta/types';
import { cn } from '@vta/ui';
import { formatProgramLabel } from '../../lib/format.ts';

/**
 * A horizontal bar that visualises program counts as proportional segments.
 *
 * Token-styled, no chart library — per the task spec we keep this simple and
 * design-system-faithful. The four canonical programs each have a static
 * Tailwind class so the JIT picks them up; an `unknown` bucket is folded
 * into a neutral border.
 */
const SEGMENT_CLASSES: Record<Program, string> = {
  v5rc: 'bg-program-v5rc',
  viqrc: 'bg-program-viqrc',
  vurc: 'bg-program-vurc',
  vairc: 'bg-program-vairc',
  unknown: 'bg-border-strong',
};

export interface ProgramBreakdownBarProps {
  /** Map of program → count. Missing keys treated as zero. */
  breakdown: Partial<Record<Program, number>>;
  className?: string;
}

const ORDER: Program[] = ['v5rc', 'viqrc', 'vurc', 'vairc', 'unknown'];

export function ProgramBreakdownBar({ breakdown, className }: ProgramBreakdownBarProps) {
  const entries = ORDER.map((p) => ({ program: p, count: breakdown[p] ?? 0 })).filter((e) => e.count > 0);
  const total = entries.reduce((s, e) => s + e.count, 0);

  if (total === 0) {
    return (
      <p className={cn('text-xs text-fg-subtle', className)}>No program breakdown available.</p>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-pill bg-bg-raised shadow-flat"
        role="img"
        aria-label="Program breakdown"
      >
        {entries.map((e) => (
          <span
            key={e.program}
            className={cn('block h-full', SEGMENT_CLASSES[e.program])}
            style={{ width: `${(e.count / total) * 100}%` }}
            title={`${formatProgramLabel(e.program, 'short')}: ${e.count}`}
          />
        ))}
      </div>
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs uppercase tracking-[0.18em] text-fg-muted">
        {entries.map((e) => (
          <li key={e.program} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', SEGMENT_CLASSES[e.program])} />
            <span>{formatProgramLabel(e.program, 'short')}</span>
            <span className="font-mono text-fg">{e.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
